'use client'

import { useGetNodeQuery } from '@/api/nodes/queries/get-node-query'
import { IChildrenProps } from '@/utils/typings/children-props'
import { createContext, RefObject, useContext, useMemo, useRef, useState } from 'react'
import { DirectoryCreateDirectoryContextProvider } from '@/features/drive/entities/directory/directory-create-directory-context'
import {
    DirectoryFileUploadContextProvider,
    IDirectoryFileUploadContextProviderProps,
} from '@/features/drive/functionalities/upload/directory-file-upload-context'
import { DirectoryHighlightedContextProvider } from '@/features/drive/entities/directory/directory-highlighted-context'
import {
    IDirectoryExPostFilters,
    useDirectoryContextContent,
} from '@/features/drive/entities/directory/utils/use-directory-context-content'
import { NodeFilePreview } from '@/features/drive/entities/node/node-preview/node-file-preview'
import { useRouter } from 'next/navigation'
import { INode } from '@/api/nodes/mutations/directory/create-directory-mutation'
import { DynamicIslandContextProvider } from '@/features/dynamic-island/dynamic-island-context'
import { DirectorySelectionDynamicIsland } from '@/features/drive/functionalities/selection/directory-selection-dynamic-island'
import { noOp } from '@/utils/functions/no-op'
import { useDriveContext } from '@/features/drive/drive-context'
import { DirectoryDragContextProvider } from '@/features/drive/functionalities/dnd/directory-drag-context'
import {
    DirectorySelectionContextProvider,
    IDirectorySelectionContextProviderProps,
} from '@/features/drive/functionalities/selection/directory-selection-context'
import { DirectoryNavigationContextProvider } from '@/features/drive/functionalities/navigation/directory-navigation-context'
import { useBrandRoute } from '@/setup/routing/use-brand-router'
import {
    createDriveFilterStore,
    IDriveFilterState,
    IDriveFilterStateValues,
} from '@/features/drive/functionalities/filters/filter-state'
import { StoreApi } from 'zustand'
import { createSafeFallback } from '@/utils/functions/create-safe-fallback'
import { datadogLogs } from '@datadog/browser-logs'

export type INodeSource = 'drive' | 'favorites'

type IDirectoryContext = {
    nodePath: string
    slugs: string[]
    setSlugs: (slugs: string[]) => void
    nodeSource: INodeSource
    isPreview: boolean
    isExcerpt: boolean
    disabledNodeUuids: string[]
    directory: INode | null | undefined
    implementation: DirectoryImplementationType
    allowFolderSelection: boolean

    content: ReturnType<typeof useDirectoryContextContent>

    setExPostFilters: (filters: IDirectoryExPostFilters) => void
    contentContainerRef: RefObject<HTMLDivElement | null>
    filterStore: StoreApi<IDriveFilterState>
}

const DirectoryContext = createContext<IDirectoryContext | null>(null)

export type IDirectoryContextProviderPropsFileUploadConfig = Omit<IDirectoryFileUploadContextProviderProps, 'children'>

export type IDirectoryContextProviderProps = IChildrenProps &
    Pick<IDirectoryContext, 'slugs' | 'implementation'> &
    Partial<
        Pick<
            IDirectoryContext,
            'setSlugs' | 'isPreview' | 'nodeSource' | 'isExcerpt' | 'disabledNodeUuids' | 'allowFolderSelection'
        >
    > & {
        fileUploadConfig?: IDirectoryContextProviderPropsFileUploadConfig
        initialFilters?: Partial<IDriveFilterStateValues>
        exPostFilters?: IDirectoryExPostFilters
        selection?: IDirectorySelectionContextProviderProps
        shouldSyncQueryParams?: boolean
    }

export enum DirectoryImplementationType {
    DRIVE = 'drive',
    HOOK_WRITER = 'hook-writer',
    VIDEO_STUDIO = 'video-studio',
    PRODUCT_DETAIL = 'product-detail',
    IMAGE_TO_VIDEO = 'image-to-video',
    TEXT_TO_IMAGE = 'text-to-image',
    VIRTUAL_TRY_ON = 'virtual-try-on',
    TEXT_TO_VIDEO = 'text-to-video',
    REMOVE_BACKGROUND = 'remove-background',
    STUDIO = 'studio',
    CHAT = 'chat',
    CREATE_CUSTOM_ACTOR = 'create-custom-actor',
    AGENT = 'agent',
    GENERATIONS = 'generations',
    LOVABLE = 'lovable',
    CONTEXT_FILE = 'context-file',
}

export const DirectoryContextProvider = ({
    children,
    slugs,
    setSlugs: propsSetSlugs,
    isPreview = false,
    nodeSource = 'drive',
    fileUploadConfig,
    disabledNodeUuids = [],
    implementation,
    initialFilters = {},
    exPostFilters: initialExPostFilters = {},
    isExcerpt = false,
    selection,
    shouldSyncQueryParams = true,
    allowFolderSelection = true,
}: IDirectoryContextProviderProps) => {
    const nodePath = useMemo(() => buildNodePath(slugs, ''), [slugs])
    const { getRoute } = useBrandRoute()

    const [exPostFilters, setExPostFilters] = useState<IDirectoryExPostFilters>(initialExPostFilters)

    const contentContainerRef = useRef<HTMLDivElement>(null)

    const filterStore = useRef(createDriveFilterStore(initialFilters, shouldSyncQueryParams)).current

    const contextContent = useDirectoryContextContent({
        nodePath: nodePath,
        filterStore,
        exPostFilters,
    })

    const router = useRouter()
    const { baseRoute } = useDriveContext()

    const setSlugs = useMemo(() => {
        if (isPreview || propsSetSlugs) {
            return propsSetSlugs || noOp
        }

        return (nextSlugs: string[]) => {
            router.push(getRoute(baseRoute).getUrl({ slugs: nextSlugs }))
        }
    }, [isPreview, propsSetSlugs, router, getRoute, baseRoute])

    const { data: directory } = useGetNodeQuery({
        nodePath,
        enabled: nodeSource === 'drive',
        onNotFound: () => setSlugs(['/']),
    })

    return (
        <DirectoryContext.Provider
            value={{
                nodePath,
                slugs,
                setSlugs,
                nodeSource,
                isPreview,
                directory,
                implementation,
                isExcerpt,
                allowFolderSelection,

                disabledNodeUuids: disabledNodeUuids,

                content: contextContent,

                setExPostFilters,

                contentContainerRef,
                filterStore,
            }}
        >
            <DirectoryNavigationContextProvider>
                <DirectorySelectionContextProvider {...selection}>
                    <DirectoryDragContextProvider>
                        <DynamicIslandContextProvider>
                            <DirectoryHighlightedContextProvider>
                                <DirectoryCreateDirectoryContextProvider>
                                    <DirectoryFileUploadContextProvider {...fileUploadConfig}>
                                        <DirectorySelectionDynamicIsland />

                                        <NodeFilePreview />

                                        {children}
                                    </DirectoryFileUploadContextProvider>
                                </DirectoryCreateDirectoryContextProvider>
                            </DirectoryHighlightedContextProvider>
                        </DynamicIslandContextProvider>
                    </DirectoryDragContextProvider>
                </DirectorySelectionContextProvider>
            </DirectoryNavigationContextProvider>
        </DirectoryContext.Provider>
    )
}

export const useDirectoryContext = () => {
    const context = useContext(DirectoryContext)

    if (!context) {
        if (process.env.NEXT_PUBLIC_ENVIRONMENT !== 'production') {
            throw new Error('DirectoryContext not found')
        }

        console.error('DirectoryContext not found')
        datadogLogs.logger.error('DirectoryContext not found')

        return createSafeFallback({}) as IDirectoryContext
    }

    return context
}

export function buildNodePathAbove(pathOrSlugs: string | string[]) {
    const currentPath = buildNodePath(pathOrSlugs, '')

    return cleanNodePath(currentPath.split('/').slice(0, -1).join('/'))
}

export function buildNodePath(pathOrSlugs: string | string[], slug: string) {
    const rawPath =
        (pathOrSlugs === '/' ? '' : '/') +
        (typeof pathOrSlugs === 'string' ? pathOrSlugs : pathOrSlugs.join('/')) +
        '/' +
        slug

    return cleanNodePath(rawPath)
}

export function cleanNodePath(path: string) {
    const rawPath = path.replaceAll('//', '/')

    const endCleaned = rawPath.endsWith('/') ? rawPath.slice(0, -1) : rawPath

    // "/" is okay, but "/a" should be "a"
    const frontCleaned = endCleaned.startsWith('/') && endCleaned.length > 1 ? endCleaned.slice(1) : endCleaned

    return frontCleaned || '/'
}
